// src/context/CartContext.js
// Uses Shopify Cart API for real cart management + checkout

import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { shopifyFetch } from '../lib/shopify';
import { useCurrency } from './CurrencyContext';
import { trackAddToCart, trackBeginCheckout, getCheckoutAttributes, decorateCheckoutUrl } from '../lib/analytics';

const CartContext = createContext();

// Gift bag add-on (SKU: MD-GIFTBAG, €2.50)
export const GIFT_BAG_VARIANT_ID = 'gid://shopify/ProductVariant/58579057639772';
export const GIFT_BAG_HANDLE = 'maison-doud-gift-bag';

const CREATE_CART = `
  mutation CartCreate($country: CountryCode!) @inContext(country: $country) {
    cartCreate(input: { buyerIdentity: { countryCode: $country } }) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                    images(first: 1) {
                      edges { node { url altText } }
                    }
                  }
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount { amount currencyCode }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const ADD_LINE = `
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!, $country: CountryCode!) @inContext(country: $country) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                    images(first: 1) {
                      edges { node { url altText } }
                    }
                  }
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount { amount currencyCode }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const UPDATE_LINE = `
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!, $country: CountryCode!) @inContext(country: $country) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                    images(first: 1) {
                      edges { node { url altText } }
                    }
                  }
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount { amount currencyCode }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const REMOVE_LINE = `
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                    images(first: 1) {
                      edges { node { url altText } }
                    }
                  }
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount { amount currencyCode }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// The only supported way to change an existing cart's currency: re-querying
// a cart with a different @inContext country has no effect once the cart's
// buyerIdentity is already locked in from creation.
const UPDATE_BUYER_IDENTITY = `
  mutation CartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!, $country: CountryCode!) @inContext(country: $country) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        id
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    handle
                    title
                    images(first: 1) {
                      edges { node { url altText } }
                    }
                  }
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount { amount currencyCode }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Saves the visitor's ad source, GA visitor ID and cookie choice onto the cart,
// so they're carried onto the Shopify order.
const UPDATE_ATTRIBUTES = `
  mutation CartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart { id }
      userErrors { field message }
    }
  }
`;

// Shopify deletes a cart once it's been checked out or left for ~10 days, and
// then answers with a userError on the cartId field ("The specified cart does
// not exist.") rather than a null cart.
function isMissingCartError(userErrors) {
    return (userErrors || []).some(e => e.field?.includes('cartId'));
}

// Reads a cart mutation's result, surfacing Shopify's userErrors as a
// thrown, readable error instead of letting a null cart crash normalizeCart.
// A deleted cart throws with `cartMissing` set so callers can start a new one.
function unwrapCartResult(result, mutationKey) {
    const { cart, userErrors } = result[mutationKey];
    if (isMissingCartError(userErrors)) {
        const err = new Error('Your cart expired. Please try again.');
        err.cartMissing = true;
        throw err;
    }
    if (userErrors && userErrors.length > 0) {
        throw new Error(userErrors[0].message);
    }
    if (!cart) {
        throw new Error('This item is no longer available.');
    }
    return cart;
}

// Normalize Shopify cart response into a clean shape
function normalizeCart(cart) {
    return {
        id: cart.id,
        checkoutUrl: cart.checkoutUrl,
        lines: cart.lines.edges.map(({ node }) => ({
            lineId: node.id,
            variantId: node.merchandise.id,
            quantity: node.quantity,
            title: node.merchandise.product.title,
            variantTitle: node.merchandise.title,
            productId: node.merchandise.product.id,
            handle: node.merchandise.product.handle,
            price: parseFloat(node.merchandise.price.amount),
            currency: node.merchandise.price.currencyCode,
            image: node.merchandise.product.images.edges[0]?.node.url || '',
            imageAlt: node.merchandise.product.images.edges[0]?.node.altText || '',
        })),
        total: parseFloat(cart.cost.totalAmount.amount),
        currency: cart.cost.totalAmount.currencyCode,
    };
}

export function CartProvider({ children }) {
    const { country } = useCurrency();
    const [cart, setCart] = useState(null);
    const [cartId, setCartId] = useState(() => localStorage.getItem('shopify_cart_id'));
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // On mount — recover existing cart from Shopify if we have a saved ID.
    // Also re-runs whenever the currency changes, migrating the existing
    // cart's buyerIdentity so its locked presentment currency matches the
    // newly selected one (cartBuyerIdentityUpdate is required for this —
    // re-querying the cart with a different @inContext country has no
    // effect on an already-created cart's currency).
    useEffect(() => {
        const savedId = localStorage.getItem('shopify_cart_id');
        if (!savedId) return;
        let cancelled = false;

        shopifyFetch(UPDATE_BUYER_IDENTITY, {
            cartId: savedId,
            buyerIdentity: { countryCode: country },
            country,
        })
            .then(data => {
                if (cancelled) return;
                const { cart, userErrors } = data.cartBuyerIdentityUpdate;
                // Cart no longer exists in Shopify (checked out or expired) — forget it
                if (isMissingCartError(userErrors)) {
                    forgetCart();
                    return;
                }
                // A failed switch is neither an expired cart nor a successful
                // migration — leave the existing cart/currency exactly as-is.
                if (userErrors && userErrors.length > 0) return;
                if (cart) setCart(normalizeCart(cart));
                else forgetCart();
            })
            .catch(() => {
                // Network error — leave cartId in place and retry next load
            });

        return () => { cancelled = true; };
    }, [country]);

    function forgetCart() {
        localStorage.removeItem('shopify_cart_id');
        setCartId(null);
        setCart(null);
    }

    // Create a new cart if we don't have one (or `fresh` to replace a deleted one)
    async function getOrCreateCart(fresh = false) {
        if (cartId && !fresh) return cartId;
        const data = await shopifyFetch(CREATE_CART, { country });
        const newCart = unwrapCartResult(data, 'cartCreate');
        localStorage.setItem('shopify_cart_id', newCart.id);
        setCartId(newCart.id);
        setCart(normalizeCart(newCart));
        return newCart.id;
    }

    // Add item to cart
    async function addToCart(variantId, quantity = 1) {
        setLoading(true);
        try {
            const addTo = async (id) => normalizeCart(unwrapCartResult(await shopifyFetch(ADD_LINE, {
                cartId: id,
                lines: [{ merchandiseId: variantId, quantity }],
                country,
            }), 'cartLinesAdd'));
            let updated;
            try {
                updated = await addTo(await getOrCreateCart());
            } catch (err) {
                if (!err.cartMissing) throw err;
                // Saved cart was deleted by Shopify — start a new one and retry once
                updated = await addTo(await getOrCreateCart(true));
            }
            setCart(updated);
            setIsOpen(true);
            trackAddToCart(variantId, quantity, updated);
        } catch (err) {
            console.error('Failed to add to cart:', err);
            toast.error(err.message || 'Could not add item to cart. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    // Update quantity of a line item
    async function updateQuantity(lineId, quantity) {
        if (!cartId) return;
        setLoading(true);
        try {
            const data = await shopifyFetch(UPDATE_LINE, {
                cartId,
                lines: [{ id: lineId, quantity }],
                country,
            });
            setCart(normalizeCart(unwrapCartResult(data, 'cartLinesUpdate')));
        } catch (err) {
            console.error('Failed to update cart:', err);
            if (err.cartMissing) forgetCart();
            toast.error(err.message || 'Could not update cart. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    // Remove a line item
    async function removeFromCart(lineId) {
        if (!cartId) return;
        setLoading(true);
        try {
            const data = await shopifyFetch(REMOVE_LINE, {
                cartId,
                lineIds: [lineId],
                country,
            });
            setCart(normalizeCart(unwrapCartResult(data, 'cartLinesRemove')));
        } catch (err) {
            console.error('Failed to remove from cart:', err);
            if (err.cartMissing) forgetCart();
            toast.error(err.message || 'Could not remove item. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    // Redirect to Shopify hosted checkout. Before leaving, record begin_checkout
    // and save attribution onto the cart. Never blocks checkout for long: the
    // attribute save is capped at 1.5s and any failure still redirects.
    // Pass the cart explicitly when calling straight after a change (Buy Now).
    async function goToCheckout(targetCart) {
        // Buttons may call this as an onClick handler, which passes a click
        // event — only accept a real cart object, otherwise use the current cart.
        if (!targetCart?.checkoutUrl) targetCart = cart;
        if (!targetCart?.checkoutUrl) return;
        trackBeginCheckout(targetCart);
        try {
            const attributes = await getCheckoutAttributes();
            if (attributes.length > 0) {
                await Promise.race([
                    shopifyFetch(UPDATE_ATTRIBUTES, { cartId: targetCart.id, attributes }),
                    new Promise(resolve => setTimeout(resolve, 1500)),
                ]);
            }
        } catch (err) {
            console.error('Failed to save checkout attributes:', err);
        }
        window.location.href = decorateCheckoutUrl(targetCart.checkoutUrl);
    }

    const cartCount = cart?.lines.reduce((sum, line) => sum + line.quantity, 0) || 0;

    return (
        <CartContext.Provider value={{
            cart,
            cartCount,
            isOpen,
            loading,
            setIsOpen,
            addToCart,
            updateQuantity,
            removeFromCart,
            goToCheckout,
        }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) throw new Error('useCart must be used within CartProvider');
    return context;
}